import React from 'react';
import { Box, Typography, Grid, Divider, Card } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { httpRequest } from 'src/services/httpRequest';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HeightIcon from '@mui/icons-material/Height';
import ScaleIcon from '@mui/icons-material/Scale';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import InfoIcon from '@mui/icons-material/Info';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import ApplicationStatus from 'src/ui-component/ApplicationStatus';
import ApplicationActions from 'src/ui-component/ApplicationActions';
import { Container } from '@mui/system';
import Breadcrumb from 'src/ui-component/Breadcrumb';

interface PetID {
  data: {
    permitId: string;
    page: number;
    rowsPerPage: number;
    petId: number;
    PetType?: string;
    PetName?: string;
    PetDOB?: string;
    PetBreed?: string;
    PetColor?: string;
    PetHair?: string;
    PetWeight?: number;
    ChipNumber?: number;
    PetSex?: string;
    IsSpayedNeutered?: boolean;
  };
}

// Custom styled component for table cells with different styles for header and body
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main || '#6E759F',
    color: theme.palette.common.white,
    fontSize: '0.9rem',
    padding: '8px',
    textTransform: 'capitalize'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '8px'
  }
}));

// Custom styled component for table rows
const StyledTableRow = styled(TableRow)(() => ({
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

function PetDetail() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = React.useState([]);
  const navigationData = location?.state && (location?.state as PetID)?.data;

  // Local pagination state for pet applications
  const [page, setPage] = useState(navigationData?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    navigationData?.rowsPerPage || 10
  );
  const [totalRecords, setTotalRecords] = useState(0);

  const columns = [
    'Application Number',
    'Applicant Name',
    'Owner Name',
    'Pet Type',
    'Pet Name',
    'Last Activity Date',
    'Permit Expiration Date',
    'Status',
    'Actions'
  ];

  const fetchListData = async () => {
    setLoading(true);
    try {
      // Construct URL with query parameters using local pagination state
      const url = `/applications/${navigationData?.permitId}/get_all?Page=${
        page + 1
      }&Size=${rowsPerPage}&Filter=ALL&PetID=${navigationData?.petId}`;

      const data = await httpRequest('GET', url);

      if (data?.status === 'success') {
        const formattedRows = [];
        const rowData = columns.map((item: string) => item.replace(/\s+/g, ''));

        data?.data?.data?.forEach((item: any) => {
          const rowObj = {};
          rowData.forEach((rowName: string) => {
            // Special handling for Status, Action, and LastActivityDate columns
            if (rowName === 'Status') {
              rowObj[rowName] = (
                <ApplicationStatus
                  status={item?.ApplicationStatus}
                  permitId={navigationData?.permitId}
                />
              );
            } else if (rowName === 'Action' || rowName === 'Actions') {
              rowObj[rowName] = (
                <ApplicationActions
                  item={item}
                  isDraft={false}
                  handleFetchApplications={fetchListData}
                />
              );
            } else if (rowName === 'LastActivityDate') {
              rowObj[rowName] = format(new Date(item.UpdateDate), 'MM/dd/yyyy');
            } else if (rowName === 'PermitExpirationDate') {
              rowObj[rowName] = item?.PermitExpirationDate ? format(
                new Date(item?.PermitExpirationDate),
                'MM/dd/yyyy'
              ): '-';
            } else {
              rowObj[rowName] = item[rowName] || '';
            }
          });
          formattedRows.push(rowObj);
        });

        setRows(formattedRows);
        // Set total records for pagination
        setTotalRecords(data?.data?.totalRecords || 0);

        // Format row data for table display
      } else {
        // Handle different types of errors
        if (data?.type === 'NETWORK_ERROR') {
          toast.error('Network error occurred');
        } else {
          toast.error(data?.message);
          dispatch(handleError(data?.message));
        }
      }
    } catch (error) {
      toast.error('An error occurred');
      dispatch(handleError('An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListData();
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function getDob() {
    if (navigationData?.PetDOB) {
      const petDOB = new Date(navigationData?.PetDOB);
      if (!isNaN(petDOB.getTime())) {
        const dob = format(petDOB, 'MM/dd/yyyy');
        return dob;
      }
    } else {
      return '';
    }
  }

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Pet License'} PageTitle={'Pet Details'} />

      <Card>
        <Box m={{ md: 3, xs: 1 }} sx={{}}>
          <Typography mb={2} variant="h4">
            Pet Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InfoIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Type:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetType}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InfoIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Name:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetName}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Date Of Birth:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {getDob()}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HeightIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Breed:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetBreed}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScaleIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Weight:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetWeight} Pound
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScaleIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Pet Hair:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetHair}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ColorLensIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Color:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetColor}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ColorLensIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Pet Sex:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.PetSex}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ColorLensIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Spayed/Neutered:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.IsSpayedNeutered ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ColorLensIcon
                  sx={{ color: 'primary.main', mr: 1, fontSize: 20 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', fontWeight: 'light' }}
                >
                  Chip Number:
                </Typography>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {navigationData?.ChipNumber}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2 }} />

          <Typography mt={4} mb={2} variant="h4">
            Application Details
          </Typography>

          <TableContainer component={Paper} className="table_container_list">
            <Table aria-label="customized table" sx={{ position: 'relative' }}>
              {/* Table Header */}
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow>
                  {columns?.map((heading, index) => (
                    <StyledTableCell
                      key={index}
                      align={heading !== 'Picture' ? 'center' : 'left'}
                    >
                      {heading}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                {rows?.length > 0 ? (
                  rows.map((row, index) => (
                    <StyledTableRow key={index}>
                      {Object.keys(row).map((key) => (
                        <React.Fragment key={key}>
                          <StyledTableCell
                            align="center"
                            sx={{ maxWidth: '30rem' }}
                          >
                            {row[key]}
                          </StyledTableCell>
                        </React.Fragment>
                      ))}
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={columns.length} align="center">
                      <Typography variant="h6">No data found</Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>

            <Divider sx={{ marginTop: 1 }} />

            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </Box>
      </Card>
    </Container>
  );
}

export default PetDetail;
